import createFetchClient from 'openapi-fetch';
import createQueryClient from 'openapi-react-query';

import { type components, type paths } from './openapi.types';
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  isAccessTokenExpired,
  updateStoredAuth,
} from '../../../providers/auth/auth-storage';

const baseUrl = process.env.REACT_APP_HOST ?? 'http://localhost:3000';
const unauthorizedEventName = 'auth:unauthorized';

const retryHeader = 'x-auth-retry';
const refreshUrl = `${baseUrl}`.replace(/\/$/, '') + '/auth/refresh';

const getHeaders = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.headers) {
    return new Headers(init.headers);
  }
  if (input instanceof Request) {
    return new Headers(input.headers);
  }
  return new Headers();
};

const withAuthHeader = (headers: Headers, accessToken: string | undefined, hadAuthHeader: boolean) => {
  if (!hadAuthHeader || !accessToken) {
    return headers;
  }
  headers.set('Authorization', `Bearer ${accessToken}`);
  return headers;
};

const tryRefreshTokens = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return undefined;
  }
  const response = await globalThis.fetch(refreshUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!response.ok) {
    return undefined;
  }
  const data = (await response.json()) as components['schemas']['AuthResponseDto'];
  if (!data?.access_token || !data?.refresh_token) {
    return undefined;
  }
  updateStoredAuth(data);
  return data.access_token;
};

const fetchWithAuth: typeof globalThis.fetch = async (input, init) => {
  const initialHeaders = getHeaders(input, init);
  const hadAuthHeader = initialHeaders.has('authorization');
  const alreadyRetried = initialHeaders.has(retryHeader);
  let accessToken = getStoredAccessToken();
  if (!alreadyRetried && accessToken && isAccessTokenExpired(accessToken)) {
    accessToken = await tryRefreshTokens();
  }
  const nextHeaders = withAuthHeader(new Headers(initialHeaders), accessToken, hadAuthHeader);
  if (alreadyRetried) {
    nextHeaders.delete(retryHeader);
  }
  const nextInit = {
    ...init,
    headers: nextHeaders,
  };
  const response = await globalThis.fetch(input, nextInit);
  if (response.status !== 401) {
    return response;
  }
  if (alreadyRetried) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(unauthorizedEventName));
    }
    return response;
  }
  const refreshedToken = await tryRefreshTokens();
  if (!refreshedToken) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(unauthorizedEventName));
    }
    return response;
  }
  const retryHeaders = withAuthHeader(new Headers(initialHeaders), refreshedToken, hadAuthHeader);
  retryHeaders.set(retryHeader, '1');
  const retryInit = {
    ...init,
    headers: retryHeaders,
  };
  return globalThis.fetch(input, retryInit);
};

export const fetch = createFetchClient<paths>({
  baseUrl,
  fetch: fetchWithAuth,
});

export const query = createQueryClient(fetch);